// 🔤 **생성형 «글자 자리»(text slot) 선언** — 회장님 2026-09-14
// ─────────────────────────────────────────────────────────────────────────────
// 시댄스 생성 영상에는 한글을 그리지 않는다(회장님 2026-08-17 · 08-19 개정). 그래서 칠판·보드·카드는
// **빈 면으로 굽고** 글자는 후편집 층이 얹는다. 그 정책의 ④항이 "빈 자리의 % 좌표를 실측·주석으로
// 박제해 후편집 삽입 좌표 정본으로" 였는데, 지금까지 그 좌표는 **사람만 읽는 주석**으로만 있었다
// (`generative-styles.ts` 퀴즈쇼 블록: "좌표(%)는 여기 주석에만 둔다"). 이 파일이 그 약속을 실행한다 —
// 같은 실측값을 **기계가 읽는 자리**로 옮긴다.
//
// ★왜 `generative-styles.ts` 가 아니라 별도 모듈인가★ — 형제 축(`GEN_FIXED_STILLS`·
//   `GEN_FIXED_CLIP_BAKED`·`GEN_CAST_PACK_ARK_ASSETS`)이 전부 이 관용구다: 골격(비트·대사·지시문)은
//   스타일 파일이 소유하고, **자산·좌표 등재는 자기 모듈**이 소유한다. 덤으로 골격 문안 핀 스위트
//   5벌을 건드리지 않고, 같은 시각 다른 스타일 블록을 만지는 여벌 레인들과 write-set 이 겹치지 않는다.
//
// ★이 선언은 «어디에 글자가 앉는가» 까지다★ — 무슨 글자가 앉는지(채움)는 소비 단계의 몫이고,
//   저장 자리는 `segment.textOverlays`(이미 `GEN_DISPLAY_SEG_KEYS`/`GEN_DISPLAY_CUT_KEYS` 등재라
//   재파생을 견딘다 · #3233 고지 캡션이 얻어 둔 자리). 새 영속 키를 만들지 않는다.
import { fixedClipAspectKey } from "./gen-fixed-line";
import { safeInset, type SafeAspect } from "../safe-zone";

/**
 * 📐 **자리 사각형** — 정규화 0..1 · **좌상단 기준**(left/top/width/height).
 *
 * ★왜 좌상단인가★: 실측 원본이 좌상단이다(`l6.5,t32,w43.75,h39.1` 류 — 프레임에서 자로 잰 값).
 *   `MbTextOverlay` 는 **중심**(x,y) 좌표계라 변환이 필요한데, 그 변환을 `slotTextAnchor` 한 곳에
 *   가두고 선언은 잰 그대로 둔다. 선언을 중심 좌표로 미리 접어 두면 원본 실측과 대조가 불가능해진다.
 */
export interface GenTextSlotRect {
  /** 왼쪽 가장자리(0..1). */
  x: number;
  /** 위 가장자리(0..1). */
  y: number;
  /** 너비(0..1). */
  w: number;
  /** 높이(0..1). */
  h: number;
}

/**
 * 🎨 **자리의 기본 글자 룩** — 스타일이 정하는 출발값이고 **유저가 덮어쓸 수 있다**.
 * 값 어휘는 `MbTextOverlay` 를 그대로 따른다(새 표시 어휘를 만들지 않는다 — 렌더 두 곳이 이미 읽는다).
 */
export interface GenTextSlotLook {
  /** 글자 높이 = sizePct × 캔버스 높이. `MbTextOverlay.sizePct` 와 같은 축. */
  sizePct: number;
  /** 글자색. */
  color: string;
  /** 검정 외곽선(제목형). */
  outline: boolean;
  /** 굵기. */
  weight: number;
  align: "left" | "center" | "right";
  /**
   * 🔤 기본 글씨체 — `caption-fonts` 카탈로그 id. 없으면 제목 폰트 기본 스택.
   * ★유저 교체가 전제다★(회장님 2026-09-14 «스타일 기본 1개 + 유저는 목록에서 교체»).
   */
  fontId?: string;
  /** 이 자리에 허용하는 최대 줄 수 — 줄바꿈 계산과 넘침 판정의 기준. */
  maxLines: number;
  /** 🌫 은은한 아래 그림자(`MbTextOverlay.shadow`) — 외곽선 대신 칠판 위에서 글자를 띄운다(2026-10-01 · 퀴즈쇼 칠판). */
  shadow?: boolean;
  /**
   * 🟨 **마지막 줄 강조색** — 글이 두 줄 이상(줄바꿈 `\n` 이 있는 원문)일 때 마지막 줄만 이 색으로 칠한다(2026-10-01 · 회장님 후보 비교 3번 «지마켓산스 + 끝 줄 노랑»).
   *   유저가 구간 색을 이미 칠했으면 그것이 이긴다(강조는 기본값일 뿐 · `buildSlotOverlay`).
   */
  accentLastLine?: string;
}

/** 🔤 **글자 자리 하나**. */
export interface GenTextSlot {
  /**
   * 배열 안에서 유일한 주소. `segment.textOverlays` 항목의 `id` 접두로도 쓴다
   * (`slot:<slotKey>`) — «내가 깐 오버레이»를 남의 오버레이와 구분하는 유일한 근거다.
   * ★`notice:` 접두를 쓰지 않는다★ — 그 접두는 고지 캡션 축의 신원이고, 겹치면 고지로 오인된다
   *   (`mediabunnyRender.MbTextOverlay.id` 주석의 두 축 충돌 경고).
   */
  slotKey: string;
  /**
   * 자리의 성격 — 채움 규칙이 이 값으로 갈린다.
   *
   * ★자리 수만큼 성격을 만들지 않는다★(2026-09-14 · 스튜디오 3인 라이브 8자리를 3종으로 접었다) —
   *   성격은 «무엇으로 채우나»의 축이지 «어디에 있나»의 축이 아니다. 회차마다 바뀌는 것이 둘
   *   (회차 제목 · 회차 한마디)이고, 나머지는 회차와 무관한 방송 장식 하나로 묶인다.
   *   - `board-question` 회차의 문제 문장(퀴즈쇼 칠판)
   *   - `headline`       회차 제목 — 무엇을 다루는 회차인가
   *   - `punch-caption`  회차의 한마디 — 지금 말하는 대목을 큰 글씨로 받는다
   *   - `chrome-badge`   회차와 무관한 방송 장식(생방송·남은 시간·연락처·마감 임박·고지)
   */
  role: "board-question" | "headline" | "punch-caption" | "chrome-badge";
  /** 유저 화면에 보일 한국어 이름. */
  label: string;
  rect: GenTextSlotRect;
  look: GenTextSlotLook;
  /**
   * 🕐 **이 자리의 글자가 나타나는 시각**(클립 상대초) — 없으면 컷 내내 떠 있다(종전 전 골격의 동작).
   *
   * ★렌더 층은 이 축을 이미 갖고 있다★ — 화면 글자 하나에 «표시 시작」이 있고 그리는 쪽이 그 값을 보고
   *   건너뛴다. 여기서 하는 일은 «자리 선언이 그 값을 들고 있게» 하는 것뿐이고, 값을 싣는 자리는
   *   `buildSlotOverlay` 한 곳이다(선언 → 화면 글자 변환의 유일한 관문).
   * ★없으면 아무것도 바뀌지 않는다★ — 미선언 자리는 변환 결과가 **종전과 바이트까지 같다**(형제 축인
   *   «꾸민 글자가 있을 때만 싣는다» 와 같은 형틀). 그래서 기존 골격 넷은 손대지 않는다.
   * ⚠️ **편집 창구의 미리보기는 이 시각을 따르지 않는다**(2026-09-16 실측 — 편집 상자 층은 씬 단위라
   *   컷 안 시각을 모른다). 유저가 편집 중에 보는 것은 «다 떠 있는 상태»이고, 완성본에서만 차례로 뜬다.
   *   자리를 잡는 데는 오히려 다 보이는 편이 낫지만, 이 어긋남을 모르면 «안 나온다»로 오인한다.
   */
  appearSec?: number;
  /**
   * ✍️ **변동 장면 글을 이 자리에 자동으로 깐다**(회장님 2026-09-23 «칠판 위치가 똑같으니 시스템으로 자동 적용») — 유저 클릭 0.
   *   `"scene-board-text"` = 고정 문장이 아닌 장면마다 그 장면 글(문제 장면은 물음 문장만 · 그 밖은 전부)을 이 자리에 앉힌다
   *   (`applyAutoBoardTextSlots` · 작업물을 열거나 글이 바뀔 때마다 · 멱등). 유저가 글을 고친 상자는 다시 덮지 않고,
   *   지운 상자는 되살리지 않는다(꺼진 상자로 남긴다). ★골격 안에서 한 자리만★ — 여럿이면 첫 자리만 쓴다.
   */
  autoFill?: "scene-board-text";
}

/**
 * ⭕ **퀴즈쇼 칠판 자리**(2026-09-23 재측정 · 회장님 «변동씬 자막 칠판에 나오게 · 칠판 위치가 똑같으니 자동 적용»).
 *
 * ★rect 출처 = 09-21 «전 컷 카메라 고정» 뒤의 변동 장면★ — 변동 장면은 구도 사진 한 장(`still-q/o/x.jpg` · 세 장 바이트 동일)을
 *   ARK 구도 참조로 싣고 굽는다. 칠판 안쪽 파란 면을 한 점에서 번져 나가는 방식(flood fill)으로 쟀다:
 *     · 구도 사진            l10.6 t30.6 w36.4 h36.6
 *     · 구운 변동 컷 2개 × 4순간(dev `sedeo_cgt-20260923101034-jiljz` · `…114423-0xugg`) l10.4~12.2 t30.2 w34.4~35.7 h36.4~37.0
 *   전 프레임의 공통 영역(l12.2~45.6 · t30.6~66.6)에 여백을 두어 자리 실측 `l15,t33,w28,h31` 이다
 *   (스위트 `gen-text-slots` 가 이 줄과 선언값을 대조한다 — 파일에서 **첫** «실측 `l…`» 줄이 지금 값이다).
 *   ⇒ 종전 값(`l7.8 t44.2 w26.5 h19.9` · 09-10 클립)은 지금 칠판의 절반 크기에 아래로 치우쳐 있었다(카메라 고정 전 구도).
 * 글자 룩은 종전 그대로(흰 글씨 + 검정 외곽선 · 굵은 둥근 고딕) — 칠판이 커져 글자를 키우고 줄 수를 늘렸다.
 * 🆕 **10-01 룩 교체**(회장님 «칠판 폰트가 예쁘지 않다» → 후보 6 비교 «3번») — 지마켓산스 · 외곽선 대신 은은한 그림자 · 끝 줄 노랑.
 *   글씨체는 자동 채움 상자에 `fontFamily` 로 실린다(`applyAutoBoardTextSlots` — 이 골격의 자동 칠판만 · 다른 골격 자리의 `fontId` 는 여전히 안 싣는다).
 *   자막 블록과 겹치지 않는 것은 렌더 직전 맞춤이 진다(`fitSlotOverlayAboveCaption`).
 */
const QUIZ_BOARD_SLOT: GenTextSlot = {
  slotKey: "board-question",
  role: "board-question",
  label: "칠판 글",
  rect: { x: 0.15, y: 0.33, w: 0.28, h: 0.31 },
  look: {
    // 폭 28%(202px) · 높이 31%(397px) 칠판 — 한 줄 약 6자 · 최대 8줄. 넘치면 shrink-to-fit 이 줄여 담는다(하한 60%).
    sizePct: 0.028,
    color: "#FFFFFF",
    outline: false,
    weight: 700,
    align: "center",
    fontId: "gmarket-sans",
    maxLines: 8,
    shadow: true,
    accentLastLine: "#FFE14D",
  },
  autoFill: "scene-board-text",
};

/** 🔤 자리 등재 — `[styleId][aspectKey][beatKey]`. 형제 매니페스트와 같은 층위 순서다. */
export const GEN_TEXT_SLOTS: Readonly<
  Record<string, Readonly<Record<string, Readonly<Record<string, readonly GenTextSlot[]>>>>>
> = {
  /**
   * ⭕ 퀴즈쇼 — 벤치 원본 v103(480×854 · 9:16).
   *
   * 🆕 **09-23 재측정 · 해설 비트 추가 · 자동 채움** — 지금 값의 정본은 위 `QUIZ_BOARD_SLOT` 주석이다(아래 09-14 서술은 역사).
   *
   * ★★rect 출처 = **우리가 굽는 클립**이다(2026-09-14 회장님 dev 육안 뒤 교체)★★
   *   종전 값(`l6.5,t32,w43.75,h39.1`)은 `generative-styles.ts` 주석의 **원본 방송 v103** 실측이었다.
   *   그런데 화면에 나오는 것은 원본 방송이 아니라 **우리가 시댄스로 새로 구운 클립**이고, 그 클립의
   *   칠판은 다른 자리에 있다 — 회장님 육안 «영역이 정확히 가운데 정렬이 안 되고».
   *   dev 회차 클립(`sedeo_cgt-20260910011253-xd7sj.mp4` 720×1280) 프레임에서 흰 판만 골라 재측정:
   *     실측 `l7.8,t44.2,w26.5,h19.9` ↔ 종전 선언은 **폭 1.65배·높이 2배**로 크고 위로 치우쳐 있었다.
   *   ⇒ 좌표 정본은 «원본 방송»이 아니라 «우리 클립»이다. 다른 스타일의 자리를 잴 때도 같은 규칙.
   * ★그래도 이 값은 «첫 제안»일 뿐이다★ — 출제 비트는 `invariantSpeech` 없는 **변동 컷**이라 회차마다
   *   새로 굽고 칠판 위치가 매번 조금씩 다르다. 정확한 자리는 유저가 미리보기 위에서 상자를 끌어 맞춘다
   *   (`SlotTextPreviewOverlay` — 왼쪽 미리보기 위). 이 선언이 하는 일은 «맨 처음 그럴듯한 자리에 놓아 주는 것»이다.
   *
   * ★look 출처★ = 원본 v103 의 생존 프레임 실측(2026-09-14 · `맥공유/퀴즈쇼-원본글자룩-참조-2026-09-14`).
   *   원본 mp4 는 make-gen-style 저작권 게이트로 삭제됐고 큐레이션 썸네일 3장만 남았다. 그중
   *   `v103_2`(정답 X 컷)에서 픽셀을 분류해 얻은 값: 흰 글씨 `#FFFFF7`(≈ 순백) · 강조어 노랑
   *   `#FFFF4A` · 외곽선 `#000800`(≈ 순흑). ⇒ 흰 글씨 + 두꺼운 검정 외곽선이 이 쇼의 글자 문법이다.
   *   ★강조어 노랑은 여기 싣지 않는다★ — `MbTextOverlay` 는 항목당 색이 하나라 «한 문장 안에서
   *   일부만 노랑」을 표현할 자리가 없다. 그리고 원본에서 그 노랑이 실제로 쓰인 자리는 **하단 해설
   *   캡션**이고, 그 축은 우리 파이프라인에 이미 있다(`drawSsulcineCaption`). 보드는 단색이다.
   *
   * ★왜 «문항 카드» 하나뿐인가(YAGNI)★ — 주석에 실측된 네 존 중 나머지 셋은 **이미 주인이 있다**:
   *   · 상단 타이틀 띠 → `postTitle`(채널 아트 축)
   *   · 정답 글리프 존 → 글자가 아니라 **무대에 실재하는 네온 조형**이다(스타일 주석의 «조형이
   *     켜진다» 교리 · 합성 글리프는 제출 가드가 무효화한다)
   *   · 하단 해설 캡션 → 하단 자막 축(회장님 2026-09-14 «자막은 그대로 둔다»)
   *   ⇒ 주인이 없는 자리는 문항 카드 하나이고, 회장님이 지목한 것도 그것이다(«칠판에 문제 글씨»).
   */
  "gen-ox-solo": {
    "9x16": {
      /** 출제 비트 — 보드에 문제가 떠 있는 구간. */
      "question-pose": [QUIZ_BOARD_SLOT],
      /** 해설 비트 — 같은 칠판·같은 자리(09-21 전 컷 카메라 고정 뒤 변동 장면은 한 구도다). */
      "explain-banner": [QUIZ_BOARD_SLOT],
    },
  },

  /**
   * 🛍 스튜디오 3인 라이브 — 벤치 원본 v005(360×640 · 9:16 · 24.53s).
   *
   * ★rect 출처★ = `generative-styles.ts` gen-live-trio 블록 주석의 실측 줄을 **그대로** 옮긴 것이다
   *   (「제목윗줄 l13.33,t14.69,…」 여덟 값). 새로 재지 않았고 반올림도 하지 않았다 — 아래 스위트가
   *   주석과 이 선언을 대조해 한쪽만 고치는 것을 막는다.
   *
   * ★왜 여덟 자리 전부인가★ — 이 골격은 원본이 **화면 글자로 말하는** 포맷이다(24초 내내 일곱 겹이
   *   떠 있다). 자리를 빼면 그만큼 원본이 사라지고, 자리는 값 등재라 늘어도 위험이 늘지 않는다.
   *   회장님 2026-09-14 «7개 전부» + 제목 두 줄 분리 승인 = 여덟.
   *
   * ★담지 못한 것(원본 실측 · 뒷사람을 위해 남긴다)★ — `GenTextSlotLook` 에는 **배경 자리가 없다**:
   *   · 남은 시간·생방송 배지의 진분홍 박스(`#D00050` 대역)
   *   · 하단 흰 띠(l2.5,t66.41,w97.5,h7.66) — 그 위의 연락처 글씨는 원본에서 남색 `#001020`·보라
   *     `#402080`·자주 `#D01040` 였다
   *   ⇒ 배경 없이 읽히도록 여덟 자리를 **흰 글씨 + 검정 외곽선**으로 통일했다(제목 아랫줄만 노랑).
   *     배경 축이 생기면 위 값으로 되살릴 수 있다. 색은 유저가 바꾼다(회장님 «스타일 기본 1개»).
   *
   * ★강조 한마디의 색★ — 원본은 앞 절반이 자주, 뒤 절반이 흰색으로 회차 안에서 갈린다. 한 자리에
   *   색이 하나라 담을 수 없어, 어떤 무대 위에서도 읽히는 흰 글씨 + 검정 외곽선으로 고정했다.
   */
  "gen-live-trio": {
    "9x16": {
      /** 원테이크 한 컷 — 이 골격은 컷이 하나뿐이라 여덟 자리가 전부 이 비트에 달린다. */
      "live-onetake": [
        {
          slotKey: "headline-top",
          role: "headline",
          label: "제목 윗줄",
          rect: { x: 0.1333, y: 0.1469, w: 0.7333, h: 0.0562 },
          look: { sizePct: 0.052, color: "#FFFFFF", outline: true, weight: 800, align: "center", fontId: "blackhansans", maxLines: 1 },
        },
        {
          slotKey: "headline-bottom",
          role: "headline",
          label: "제목 아랫줄",
          // 원본 실측 노랑 — 양자화 버킷 `#F0F010`(R·G 240~255 · B 16~31)의 중앙값.
          rect: { x: 0.075, y: 0.2172, w: 0.85, h: 0.0562 },
          look: { sizePct: 0.052, color: "#FFFF1A", outline: true, weight: 800, align: "center", fontId: "blackhansans", maxLines: 1 },
        },
        {
          slotKey: "timer-badge",
          role: "chrome-badge",
          label: "남은 시간",
          // rect 는 분홍 박스 전체이고 sizePct 는 그 안 글자 높이다(박스 = 글자가 아니다).
          rect: { x: 0.0167, y: 0.2969, w: 0.3361, h: 0.0516 },
          look: { sizePct: 0.03, color: "#FFFFFF", outline: true, weight: 700, align: "center", fontId: "do-hyeon", maxLines: 1 },
        },
        {
          slotKey: "live-badge",
          role: "chrome-badge",
          label: "생방송 표시",
          rect: { x: 0.8889, y: 0.2969, w: 0.0889, h: 0.0219 },
          look: { sizePct: 0.017, color: "#FFFFFF", outline: true, weight: 700, align: "center", fontId: "do-hyeon", maxLines: 1 },
        },
        {
          slotKey: "punch-caption",
          role: "punch-caption",
          label: "강조 한마디",
          // 다섯 프레임에서 같은 대역으로 재현된 값(한 줄 회차는 이 띠의 아래 절반에 앉는다).
          rect: { x: 0.0111, y: 0.4531, w: 0.9778, h: 0.2031 },
          look: { sizePct: 0.062, color: "#FFFFFF", outline: true, weight: 800, align: "center", fontId: "one-store-mobile-pop", maxLines: 2 },
        },
        {
          slotKey: "contact-bar",
          role: "chrome-badge",
          label: "연락처 띠",
          rect: { x: 0, y: 0.6688, w: 0.4444, h: 0.0375 },
          look: { sizePct: 0.034, color: "#FFFFFF", outline: true, weight: 700, align: "left", fontId: "do-hyeon", maxLines: 1 },
        },
        {
          slotKey: "closing-badge",
          role: "chrome-badge",
          label: "마감 임박",
          // 원본은 3초 켜짐·2초 꺼짐으로 깜빡인다(f001 존재 · f015 부재). 깜빡임 축이 없어 상시 표시다.
          rect: { x: 0.6389, y: 0.6891, w: 0.3361, h: 0.0375 },
          look: { sizePct: 0.034, color: "#FFFFFF", outline: true, weight: 800, align: "center", fontId: "do-hyeon", maxLines: 1 },
        },
        {
          slotKey: "disclaimer-line",
          role: "chrome-badge",
          label: "고지 한 줄",
          rect: { x: 0.0083, y: 0.7203, w: 0.5028, h: 0.0219 },
          look: { sizePct: 0.018, color: "#FFFFFF", outline: true, weight: 500, align: "left", fontId: "do-hyeon", maxLines: 1 },
        },
      ],
    },
  },

  /**
   * 🪂 활공 중 외침 — 벤치 원본 v006(360×640 · 9:16 · 21.91s).
   *
   * ★rect 출처★ = `generative-styles.ts` gen-freefall-shout 블록 주석의 실측 줄을 **그대로** 옮긴 것이다
   *   (「제목윗줄 l7.22,t16.56,…」 세 값). 새로 재지 않았고 반올림도 하지 않았다 — 아래 스위트가 주석과
   *   이 선언을 대조해 한쪽만 고치는 것을 막는다(형제 v005 와 같은 규약).
   *
   * ★왜 셋뿐인가★ — 원본 화면에 떠 있는 글자는 네 겹인데 하나는 이미 주인이 있다:
   *   · 상단 제목 두 줄 → 주인 없음 → 자리 둘(원본에서 «흰 줄 + 노란 줄» 이 아니라 ★한 줄 안에서★
   *     노랑과 흰색이 섞이는데, 항목당 색이 하나라 둘 다 흰색으로 고정했다)
   *   · 내지를 때 화면 한가운데 뜨는 큰 글씨 → 주인 없음 → 자리 하나(`punch-caption`)
   *   · 하단 대사 자막 → **이미 주인이 있다**(`drawSsulcineCaption` · 하단 자막 축) → 자리를 만들지 않는다
   *   회장님 2026-09-15 «3개» 확정.
   *
   * ★look 출처★ = 원본 프레임 픽셀 분류(f008 제목 · f016 큰 외침). 제목 노랑은 `#FFFF0D`, 흰색은
   *   `#FFFFFF`, 외곽선은 검정이었다. 큰 외침은 흰 글씨 + 두꺼운 검정 외곽선 한 가지뿐이다.
   *   ★배경 자리가 없다★ — `GenTextSlotLook` 에는 박스 배경 축이 없어 제목 뒤의 검은 띠는 담지 못한다.
   *   그래서 셋 다 어떤 하늘 위에서도 읽히도록 흰 글씨 + 검정 외곽선으로 통일했다(색은 유저가 바꾼다).
   */
  "gen-freefall-shout": {
    "9x16": {
      /** 원테이크 한 컷 — 이 골격은 컷이 하나뿐이라 세 자리가 전부 이 비트에 달린다. */
      "freefall-onetake": [
        {
          slotKey: "headline-top",
          role: "headline",
          label: "제목 윗줄",
          rect: { x: 0.0722, y: 0.1656, w: 0.85, h: 0.0516 },
          look: { sizePct: 0.048, color: "#FFFFFF", outline: true, weight: 800, align: "center", fontId: "blackhansans", maxLines: 1 },
        },
        {
          slotKey: "headline-bottom",
          role: "headline",
          label: "제목 아랫줄",
          rect: { x: 0.2194, y: 0.2344, w: 0.5528, h: 0.0516 },
          look: { sizePct: 0.048, color: "#FFFFFF", outline: true, weight: 800, align: "center", fontId: "blackhansans", maxLines: 1 },
        },
        {
          slotKey: "punch-caption",
          role: "punch-caption",
          // 내지르는 한마디가 뜨는 자리 — 원본 f016(「뭘 고민해」)에서 잰 한 줄 띠다. 두 줄 회차는
          // shrink-to-fit 이 줄여 담는다(자리를 두 줄 높이로 부풀리면 얼굴을 덮는다).
          label: "내지르는 한마디",
          rect: { x: 0, y: 0.5781, w: 0.9222, h: 0.1203 },
          look: { sizePct: 0.085, color: "#FFFFFF", outline: true, weight: 800, align: "center", fontId: "one-store-mobile-pop", maxLines: 2 },
        },
      ],
    },
  },

  /**
   * 📹 중계에 잡힌 관중석 — 벤치 원본 v014(360×640 · 9:16 · 24.11s).
   *
   * ★rect 출처 = **원본 프레임 실측**이다(2026-09-16 · 세션 「36.포맷 v014 스포츠 경기」)★
   *   `generative-styles.ts` 의 `gen-crowd-cam` 블록 주석과 같은 측정에서 나왔다. 컷1 은 6.0s 프레임,
   *   컷2 는 22.0s 프레임에서 밝은 화소만 골라 글자 덩어리의 경계 상자를 잰 값이다:
   *     · 컷1 제목윗줄   실측 `l28.33,t16.56,w42.78,h3.75`
   *     · 컷1 제목아랫줄 실측 `l6.67,t21.88,w86.67,h3.75`
   *     · 컷1 생방송배지 실측 `l87.22,t30.16,w6.67,h1.88`(빨간 박스 · R>140·G<80·B<80)
   *     · 컷2 마무리윗줄 실측 `l10.83,t17.97,w80.28,h3.91`
   *     · 컷2 마무리큰줄 실측 `l24.17,t67.19,w73.33,h4.38`
   *
   * ⚠️ ★이 값은 «첫 제안»이고, 우리 클립으로 다시 재야 한다★ — 좌표 정본은 «원본 방송»이 아니라
   *   «우리가 굽는 클립»이다(퀴즈쇼 블록이 2026-09-14 회장님 육안 뒤 배운 규칙). 이 골격은 아직
   *   여벌 자산을 굽지 않아 우리 클립이 없다. 유료 1컷이 나오면 같은 방법으로 재측정해 이 값을
   *   갈아끼운다. 그때까지는 «맨 처음 그럴듯한 자리에 놓아 주는 것»까지가 이 선언의 몫이고,
   *   정확한 자리는 유저가 왼쪽 미리보기에서 상자를 끌어 맞춘다(`SlotTextPreviewOverlay`).
   *
   * ★왜 이 다섯인가★ — 원본 화면에 겹쳐 있는 글자 층은 여섯인데, 그중 **하단 대사 자막은 이미 주인이
   *   있다**(`drawSsulcineCaption` · 회장님 2026-09-14 «자막은 그대로 둔다»). 주인이 없는 다섯만 싣는다.
   *   ★좌상단 점수판은 싣지 않는다★ — 팀명·이닝·점수가 칸으로 나뉜 **표**라 항목 하나에 글자 하나인
   *   `MbTextOverlay` 로는 담을 자리가 없다(같은 이유로 `gen-live-trio` 가 분홍 박스 배경을 못 담았다).
   *   유저가 필요하면 [＋ 글자 추가]로 직접 만든다(09-15 전 스타일 개방).
   *
   * ★look★ — 원본은 다섯 자리 모두 굵은 고딕 + 검정 외곽선이다. 제목 아랫줄만 노랑인데, 실측 중앙값이
   *   `#FFFF16` 이었다(x 36.4~72.2% 구간 · 나머지는 흰색). ★한 자리에 색이 하나★ 라 그 줄 전체를 노랑으로
   *   고정했다(형제 `gen-live-trio` 와 같은 처리). 색·글씨체는 유저가 바꾼다.
   */
  "gen-crowd-cam": {
    "9x16": {
      /** 관중석 포착 — 제목 두 줄이 붙어 있고 우상단에 생방송 배지가 뜬다. */
      "stand-catch": [
        {
          slotKey: "headline-top",
          role: "headline",
          label: "제목 윗줄",
          rect: { x: 0.2833, y: 0.1656, w: 0.4278, h: 0.0375 },
          look: { sizePct: 0.0375, color: "#FFFFFF", outline: true, weight: 800, align: "center", fontId: "blackhansans", maxLines: 1 },
        },
        {
          slotKey: "headline-bottom",
          role: "headline",
          label: "제목 아랫줄",
          rect: { x: 0.0667, y: 0.2188, w: 0.8667, h: 0.0375 },
          look: { sizePct: 0.0375, color: "#FFFF16", outline: true, weight: 800, align: "center", fontId: "blackhansans", maxLines: 1 },
        },
        {
          slotKey: "live-badge",
          role: "chrome-badge",
          label: "생방송 표시",
          // rect 는 빨간 박스 전체이고 sizePct 는 그 안 글자 높이다(박스 = 글자가 아니다).
          rect: { x: 0.8722, y: 0.3016, w: 0.0667, h: 0.0188 },
          look: { sizePct: 0.015, color: "#FFFFFF", outline: true, weight: 700, align: "center", fontId: "do-hyeon", maxLines: 1 },
        },
      ],
      /** 중계석 마무리 카드 — 윗줄이 질문을 던지고 아래 큰 글씨가 받는다(2026-09-17 재설계로 비트 키가 `back-to-play` → `booth-turn`. 자리·크기는 그대로). */
      "booth-turn": [
        {
          slotKey: "closing-headline",
          role: "headline",
          label: "마무리 윗줄",
          rect: { x: 0.1083, y: 0.1797, w: 0.8028, h: 0.0391 },
          look: { sizePct: 0.0391, color: "#FFFFFF", outline: true, weight: 800, align: "center", fontId: "blackhansans", maxLines: 1 },
        },
        {
          slotKey: "closing-punch",
          role: "punch-caption",
          label: "마무리 한마디",
          rect: { x: 0.2417, y: 0.6719, w: 0.7333, h: 0.0438 },
          look: { sizePct: 0.0438, color: "#FFFFFF", outline: true, weight: 800, align: "center", fontId: "one-store-mobile-pop", maxLines: 2 },
        },
      ],
    },
  },
  /**
   * 🪧 솟아올라 멈추는 안내 — 벤치 아카이브 v015(360×640 · 9:16).
   *
   * ★이 골격은 «글자가 차례로 쌓여 한 장이 된다»가 형식의 절반이다★ — 그래서 자리마다 `appearSec`(등장 시각)을
   *   같이 선언한다. 벤치 컷1(6초)에서 잰 값이 그대로 근거다:
   *     제목 띠 **0초**(컷 내내 고정) · 짧은 딱지 **0.6초** · 비교 줄 **0.6초** · 한마디 **2.6초**.
   *   영상은 2.0초에 멈추고, 마지막 한마디가 2.6초에 얹히면 남은 3.3초가 «읽는 시간»이 된다.
   *
   * ⚠️★rect 는 아직 «원본 기준 첫 제안»이다 — 우리 클립 실측이 아니다★⚠️
   *   좌표 정본은 «우리가 굽는 클립»이어야 한다(형제 축이 원본 방송 기준 값을 쓰다 폭이 1.65배 어긋난 선례).
   *   이 골격은 아직 클립을 굽지 않았으므로 벤치 프레임에서 잰 배치를 그대로 옮겨 **출발점**으로만 둔다.
   *   ⇒ dev 유료 1컷을 구운 뒤 그 프레임에서 다시 재어 이 값을 갈아끼우고, 그때 이 단서를 지운다.
   *   그래도 화면에서 깨지지 않는다 — 유저가 미리보기 위에서 상자를 끌어 맞추는 것이 전제다.
   *
   * 원본 실측(360×640 픽셀 → 정규화): 제목 띠 `l20,t96,w320,h84` · 짧은 딱지 `l120,t392,w130,h44` ·
   *   비교 줄 `l25,t436,w315,h44` · 한마디 `l55,t480,w250,h36`.
   *
   * ⚠️★2026-09-23 — 네 자리를 **세이프존 SSOT(`src/lib/safe-zone.ts`)의 `core` 안으로** 다시 쌓았다★
   *   종전 값(원본 기준 첫 제안)은 9:16 에서 **상 21.09%·하 26.82%·오른쪽 14.75%** 를 침범했다 —
   *   플랫폼 UI 에 실제로 먹히는 자리였다. core = 상 0.2109 · 하 0.7318 · 좌 0.0553 · 우 0.8525 이고,
   *   그 안에서 제목 → 딱지 → 견주는 줄 → 한마디 순으로 겹치지 않게 놓았다.
   *
   * ⚠️(낡음) 한마디 자리만 원본보다 **살짝 위로** 옮겼다 — 원본 값(`t480,h36` = 0.750~0.806)은 우리 화면의
   *   **하단 자막 띠**(아래 20%)를 침범한다. 원본에는 그 띠가 없지만 우리는 자막을 그대로 두기로 했으므로
   *   (회장님 2026-09-14) 두 축이 같은 픽셀을 다투지 않게 0.752~0.798 로 눌렀다. 기하 불변식이 이걸 잡는다.
   */
  "gen-poster-reveal": {
    "9x16": {
      /** 솟아 자리 잡는다 — 네 자리가 모두 사는 원형 컷. */
      "rise-settle": [
        {
          slotKey: "headline-band",
          role: "headline",
          label: "제목 띠",
          // 두 줄 대역(원본도 두 줄이다). 컷이 시작할 때부터 떠 있어 `appearSec` 을 두지 않는다.
          rect: { x: 0.0553, y: 0.2109, w: 0.7972, h: 0.118 },
          look: { sizePct: 0.045, color: "#FFFFFF", outline: true, weight: 800, align: "center", fontId: "yeogi-ottae-jalnan", maxLines: 2 },
        },
        {
          slotKey: "tag-badge",
          role: "chrome-badge",
          label: "짧은 딱지",
          // 한 줄짜리 작은 딱지. 넣을 문안은 유저 몫이고 스타일은 «짧게 한 줄» 만 정한다.
          rect: { x: 0.3039, y: 0.548, w: 0.3, h: 0.052 },
          look: { sizePct: 0.03, color: "#FFFFFF", outline: true, weight: 800, align: "center", fontId: "do-hyeon", maxLines: 1 },
          appearSec: 0.6,
        },
        {
          slotKey: "compare-line",
          role: "punch-caption",
          label: "견주는 줄",
          rect: { x: 0.0553, y: 0.6080, w: 0.7972, h: 0.0620 },
          look: { sizePct: 0.04, color: "#FFFFFF", outline: true, weight: 800, align: "center", fontId: "yeogi-ottae-jalnan", maxLines: 1 },
          appearSec: 0.6,
        },
        {
          slotKey: "punch-bubble",
          role: "punch-caption",
          label: "한마디",
          // 영상이 멈춘 뒤(2.0초) 마지막으로 얹히는 자리 — 이 한 줄이 «읽는 시간»의 시작점이다.
          rect: { x: 0.1539, y: 0.6760, w: 0.6000, h: 0.0460 },
          look: { sizePct: 0.03, color: "#FFFFFF", outline: true, weight: 700, align: "center", fontId: "do-hyeon", maxLines: 1 },
          appearSec: 2.6,
        },
      ],
      /** 가까이서 하나 더 — 제목 띠는 그대로 남고 한마디만 새로 얹힌다(가운데 컷은 자리를 늘리지 않는다). */
      "next-rise": [
        {
          slotKey: "headline-band",
          role: "headline",
          label: "제목 띠",
          rect: { x: 0.0553, y: 0.2109, w: 0.7972, h: 0.118 },
          look: { sizePct: 0.045, color: "#FFFFFF", outline: true, weight: 800, align: "center", fontId: "yeogi-ottae-jalnan", maxLines: 2 },
        },
        {
          slotKey: "punch-bubble",
          role: "punch-caption",
          label: "한마디",
          rect: { x: 0.1539, y: 0.6760, w: 0.6000, h: 0.0460 },
          look: { sizePct: 0.03, color: "#FFFFFF", outline: true, weight: 700, align: "center", fontId: "do-hyeon", maxLines: 1 },
          appearSec: 2.6,
        },
      ],
      /** 나란히 놓여 멈춘다 — 마무리. 제목 띠 + 닫는 한마디. */
      "lineup-freeze": [
        {
          slotKey: "headline-band",
          role: "headline",
          label: "제목 띠",
          rect: { x: 0.0553, y: 0.2109, w: 0.7972, h: 0.118 },
          look: { sizePct: 0.045, color: "#FFFFFF", outline: true, weight: 800, align: "center", fontId: "yeogi-ottae-jalnan", maxLines: 2 },
        },
        {
          slotKey: "punch-bubble",
          role: "punch-caption",
          label: "닫는 한마디",
          rect: { x: 0.1539, y: 0.6760, w: 0.6000, h: 0.0460 },
          look: { sizePct: 0.03, color: "#FFFFFF", outline: true, weight: 700, align: "center", fontId: "do-hyeon", maxLines: 1 },
          appearSec: 2.6,
        },
      ],
    },
  },
};

export type GenTextSlotManifest = typeof GEN_TEXT_SLOTS;

/** 🔎 조회 키 — 화면비는 형제 매니페스트와 같은 정규화(`9:16` → `9x16`)를 쓴다. */
export interface GenTextSlotQuery {
  styleId: string;
  aspect: string;
  beatKey: string;
}

/**
 * 🔎 **이 비트에 선언된 글자 자리** — 없으면 빈 배열(호출부에 null 검사를 강요하지 않는다).
 *
 * ★조용히 폴백하지 않는다★ — 화면비가 다르면 «없다» 로 답한다. 9:16 으로 잰 좌표를 16:9 에
 *   그대로 얹으면 글자가 보드 밖에 앉는데, 그건 자리가 없는 것보다 나쁘다(유저는 틀린 자리를
 *   «기능이 깨졌다» 로 읽는다). 비율마다 재서 등재하는 것이 이 매니페스트의 계약이다.
 */
export function genTextSlotsFor(
  q: GenTextSlotQuery,
  manifest: GenTextSlotManifest = GEN_TEXT_SLOTS,
): readonly GenTextSlot[] {
  const styleId = String(q?.styleId ?? "").trim();
  const beatKey = String(q?.beatKey ?? "").trim();
  const aspectKey = fixedClipAspectKey(String(q?.aspect ?? "").trim());
  if (!styleId || !beatKey || !aspectKey) return [];
  return slotsInSafeZone(manifest[styleId]?.[aspectKey]?.[beatKey] ?? [], aspectKey);
}

/**
 * 📐 **화면비 키 → 세이프존 축의 표기**. 세이프존 정본(`../safe-zone`)은 사람이 읽는 `9:16` 을 쓰고
 * 이 매니페스트는 형제 매니페스트와 맞춘 파일명 표기 `9x16` 을 쓴다. 표기를 통일하지 않고 **여기서만**
 * 번역한다 — 두 표기에 각자 이유가 있다(형제 축 정합 ↔ 정본 가독성).
 */
const SAFE_ASPECT_OF_KEY: Readonly<Record<string, SafeAspect>> = { "9x16": "9:16", "16x9": "16:9" };

/**
 * 📐 조회 결과 캐시 — 아래 참조 안정성 문단 참고. 매니페스트가 상수라 배열이 키로 적합하다.
 */
const SAFE_SLOT_CACHE = new WeakMap<readonly GenTextSlot[], readonly GenTextSlot[]>();

/**
 * 📐 **선언 자리를 세이프존 안으로 접는다** — 회장님 2026-09-23 «세이프존을 지켜서 만들고, 전역에».
 *
 * ★선언값은 고치지 않는다★ — 위 `rect` 들은 **벤치 원본에서 자로 잰 기록**이고, 스위트가
 *   `generative-styles.ts` 주석의 실측 줄과 대조해 한쪽만 바뀌는 것을 막는다. 덮어쓰면 «원본이 어땠나»를
 *   잃는다. ⇒ 기록은 그대로 두고 **밖으로 나가는 값만** 접는다.
 * ★왜 접는 자리가 여기인가★ — `genTextSlotsFor` 가 이 매니페스트의 **유일한 출구**다(src 전수 확인:
 *   `GEN_TEXT_SLOTS` 를 직접 읽는 코드 0 · 소비는 `gen-text-slot-store` 두 줄뿐). 출구가 하나라
 *   편집창 미리보기·캔버스·remotion 이 **같은 값**을 본다 — 소비처마다 접으면 생기는 «미리보기와
 *   결과물이 갈린다»가 구조적으로 성립하지 않는다(`gen-text-slot-fit` 의 제1 불변식과 같은 형틀).
 * ★유저가 끌어 만든 상자는 접지 않는다★ — 그건 `gen-text-slot-store` 의 `over.rect` 축이라 여기 오지
 *   않는다. 유저가 놓아 둔 자리를 말없이 옮기면 «내가 놓은 데 없다»가 된다. 그쪽은 미리보기에 안전
 *   구역을 **보여 주는** 것이 맞는 처방이고 별건이다.
 * ★안 움직이면 원 배열을 그대로 돌려준다★ — 참조까지 종전과 같아, 이미 안에 있던 골격은 이 변경으로
 *   1바이트도 달라지지 않는다. 움직인 배열만 캐시해 두는 것도 같은 이유다(매번 새 배열을 내면
 *   호출부의 참조 비교·메모가 매 렌더 깨진다).
 *
 * ★★자리를 **하나씩** 접으면 안 된다★★ — 2026-09-23 실측에서 드러났다. `gen-live-trio` 는 제목이
 *   윗줄·아랫줄 두 자리로 붙어 있는데(y 0.1469 / 0.2172), 윗줄만 안전 상단 0.2109 로 밀리면
 *   **아랫줄과 포개진다**. 이 골격들은 «층이 여럿 쌓인 화면»이 본체라 **층 사이 관계가 곧 레이아웃**이고,
 *   개별 clamp 는 그 관계를 깨뜨린다. ⇒ 비트의 자리 전부를 한 덩어리로 보고 **같은 변환**(균일 축소 +
 *   이동)을 먹인다. 상대 배치가 보존되고 겹침이 생기지 않는다.
 * ★글자 크기도 같은 배율로 내린다★ — 상자만 줄이면 같은 글이 더 많은 줄로 접혀 넘친다. 배율을 같이
 *   태우면 줄 수가 보존된다. (포스터 장표에서 손으로 했던 처분과 같은 성격 — 안전 폭만 지키고 크기를
 *   두면 «안 잘리지만 못 읽는» 화면이 된다.) 남는 넘침은 `wrapTextForSlot` 의 shrink-to-fit 몫이다.
 * ★키우지는 않는다★ — 배율 상한이 1 이다. 원본보다 크게 벌리면 그건 접기가 아니라 재배치다.
 */
function slotsInSafeZone(slots: readonly GenTextSlot[], aspectKey: string): readonly GenTextSlot[] {
  const aspect = SAFE_ASPECT_OF_KEY[aspectKey];
  if (!aspect || !slots.length) return slots;
  const hit = SAFE_SLOT_CACHE.get(slots);
  if (hit) return hit;

  const s = safeInset(aspect, "core");
  // 🔲 이 비트의 자리 전부를 감싸는 한 덩어리.
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const sl of slots) {
    x0 = Math.min(x0, sl.rect.x);
    y0 = Math.min(y0, sl.rect.y);
    x1 = Math.max(x1, sl.rect.x + sl.rect.w);
    y1 = Math.max(y1, sl.rect.y + sl.rect.h);
  }
  // 덩어리가 이미 전부 안에 있으면 **손대지 않는다** — 원 배열을 그대로 돌려줘 참조까지 종전과 같다.
  //   (이 갈래를 먼저 두는 이유: 아래 중심 기준 계산은 배율 1 에서도 부동소수점 잔차를 남긴다.)
  const eps = 1e-9;
  if (x0 >= s.left - eps && y0 >= s.top - eps && x1 <= 1 - s.right + eps && y1 <= 1 - s.bottom + eps) {
    SAFE_SLOT_CACHE.set(slots, slots);
    return slots;
  }

  const bw = Math.max(1e-6, x1 - x0);
  const bh = Math.max(1e-6, y1 - y0);
  const k = Math.min(1, (1 - s.left - s.right) / bw, (1 - s.top - s.bottom) / bh);
  const nw = bw * k;
  const nh = bh * k;
  // 줄일 때는 **덩어리 중심을 붙들고** 줄인다(한쪽으로 쏠리지 않게), 그 뒤 안전 구역 안으로 민다.
  const nx = Math.min(Math.max(x0 + bw / 2 - nw / 2, s.left), 1 - s.right - nw);
  const ny = Math.min(Math.max(y0 + bh / 2 - nh / 2, s.top), 1 - s.bottom - nh);

  const dx = nx - x0 * k;
  const dy = ny - y0 * k;
  const out = Object.freeze(
    slots.map((slot) => ({
      ...slot,
      rect: { x: slot.rect.x * k + dx, y: slot.rect.y * k + dy, w: slot.rect.w * k, h: slot.rect.h * k },
      look: { ...slot.look, sizePct: slot.look.sizePct * k },
    })),
  );
  SAFE_SLOT_CACHE.set(slots, out);
  return out;
}

/** 🔎 이 스타일·화면비에 글자 자리가 하나라도 선언돼 있는가 — 창구를 열지 말지의 판정. */
export function genStyleHasTextSlots(
  styleId: string,
  aspect: string,
  manifest: GenTextSlotManifest = GEN_TEXT_SLOTS,
): boolean {
  const byBeat = manifest[String(styleId ?? "").trim()]?.[fixedClipAspectKey(String(aspect ?? "").trim())];
  if (!byBeat) return false;
  return Object.values(byBeat).some((slots) => Array.isArray(slots) && slots.length > 0);
}

/**
 * 🔎 **이 스타일·화면비에서 이 키로 선언된 자리**(비트 무관) — 저장된 상자가 «순서로 매긴 비트」와 다른 장면에 붙어 있어도
 *   같은 자리·같은 룩으로 풀리게 한다(자동 채움은 순서가 아니라 **내용**으로 장면을 고른다 · 2026-09-23).
 */
export function genTextSlotByKey(
  styleId: string,
  aspect: string,
  slotKey: string,
  manifest: GenTextSlotManifest = GEN_TEXT_SLOTS,
): GenTextSlot | null {
  const byBeat = manifest[String(styleId ?? "").trim()]?.[fixedClipAspectKey(String(aspect ?? "").trim())];
  const key = String(slotKey ?? "").trim();
  if (!byBeat || !key) return null;
  for (const slots of Object.values(byBeat)) for (const s of slots ?? []) if (s.slotKey === key) return s;
  return null;
}

/** ✍️ 이 스타일·화면비의 **자동 채움 자리**(`autoFill`) — 없으면 null(자동 채움이 서지 않는다). */
export function genAutoFillTextSlot(
  styleId: string,
  aspect: string,
  manifest: GenTextSlotManifest = GEN_TEXT_SLOTS,
): GenTextSlot | null {
  const byBeat = manifest[String(styleId ?? "").trim()]?.[fixedClipAspectKey(String(aspect ?? "").trim())];
  if (!byBeat) return null;
  for (const slots of Object.values(byBeat)) for (const s of slots ?? []) if (s.autoFill) return s;
  return null;
}

/**
 * 📐 **자리 → `MbTextOverlay` 의 중심 좌표**.
 *
 * `drawTextOverlays`(캔버스)·`TextOverlayLabelNode`(remotion) 둘 다 `y` 를 **글자 블록의 세로 중심**
 * 으로 읽고 거기서 위아래로 펼친다. 그래서 자리의 세로 중심을 그대로 준다.
 * 가로는 정렬을 따른다 — `center` 는 자리의 가로 중심, `left`/`right` 는 그 가장자리다
 * (두 렌더러 모두 `align` 을 기준점의 의미로 쓴다).
 */
export function slotTextAnchor(slot: Pick<GenTextSlot, "rect" | "look">): { x: number; y: number } {
  const { rect, look } = slot;
  const y = rect.y + rect.h / 2;
  const x = look.align === "left" ? rect.x : look.align === "right" ? rect.x + rect.w : rect.x + rect.w / 2;
  return { x, y };
}

/**
 * 🆔 이 자리가 만든 오버레이의 id — 편집 창구가 «내가 깐 것» 을 알아보는 근거.
 * ★`notice:` 접두와 겹치지 않는다★(위 `slotKey` 주석의 두 축 충돌).
 */
export const GEN_TEXT_SLOT_ID_PREFIX = "slot:";

/** 🆔 자리 id → 오버레이 id. */
export function slotOverlayId(slotKey: string): string {
  return `${GEN_TEXT_SLOT_ID_PREFIX}${String(slotKey ?? "").trim()}`;
}

/** 🆔 이 오버레이가 «글자 자리»가 깐 것인가. */
export function isGenTextSlotOverlay(o: unknown): boolean {
  const id = (o as { id?: unknown } | null)?.id;
  return typeof id === "string" && id.startsWith(GEN_TEXT_SLOT_ID_PREFIX);
}
